import { useQueryClient } from '@tanstack/react-query';
import { App, Spin } from 'antd';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import type { BtlApplication } from '@api/btlApplicationForm/types';
import { ApplicationStatus, AssertionType, PropertyType } from '@api/btlApplicationForm/types';
import { shouldBlockSubmissionForMissingFields } from '@common/missingFields';
import {
  getAdjacentOoCaseSidebarItemId,
  getOoCaseSidebarNavigationLabel,
} from '@components/CaseSidebar/navigation/ooCaseSidebarNavigation';
import type { OoCaseSidebarContext } from '@components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { FormValidationContext } from '@hooks/useBtlFormValidation';
import { additionalInformationFormSchema } from '@pages/BtlApplicationFormPage/AdditionalInformation/formSchema';
import { BtlBaseForm } from '@pages/BtlApplicationFormPage/BtlBaseForm';
import { invalidateBtlApplicationData } from '@pages/BtlApplicationFormPage/btl-utils';
import { makeFormReadonly } from '@pages/BtlApplicationFormPage/makeFormReadonly';
import { CUSTOMER_DECLARATION_ACCEPTED, submitDipFormSchema } from '@pages/BtlApplicationFormPage/SubmitDip/formSchema';
import { useBtlMissingFields } from '@queries/btlApplications/useBtlMissingFields';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { config } from '@utils/config';
import { OoCaseFormFooter } from '../OoCaseFormFooter';
import { standardFormValidationProps } from '@common/formValidation';

type OoSubmitVariant = 'DIP' | 'APPLICATION';
type DipSubmitCheckboxValue = 'privacyAccepted' | 'creditConsentAccepted';

interface CaseSubmitPageProps {
  caseId: string;
  caseData?: BtlApplication;
  isCaseLoading: boolean;
  sidebarContext: OoCaseSidebarContext;
  variant: OoSubmitVariant;
}

const SUBMIT_SECTION_BY_VARIANT: Record<OoSubmitVariant, CaseSidebarItemId> = {
  DIP: CaseSidebarItemId.SUBMIT_DIP,
  APPLICATION: CaseSidebarItemId.SUBMIT_APPLICATION,
};

const SUBMIT_LABEL_BY_VARIANT: Record<
  OoSubmitVariant,
  'btlApplicationForm.SUBMIT_DIP.label' | 'caseSidebar.pages.submitApplication'
> = {
  DIP: 'btlApplicationForm.SUBMIT_DIP.label',
  APPLICATION: 'caseSidebar.pages.submitApplication',
};

function hydrateDipSubmitCheckboxes(caseData: BtlApplication | undefined): DipSubmitCheckboxValue[] {
  const checkedAssertions = [...new Set(caseData?.assertions?.assertions?.map(assertion => assertion.type) ?? [])];
  const dipAssertions = checkedAssertions.filter(
    assertion => assertion === AssertionType.PRIVACY || assertion === AssertionType.CREDIT_CONSENT,
  );

  if (dipAssertions.length > 0) {
    return [
      dipAssertions.includes(AssertionType.PRIVACY) ? 'privacyAccepted' : null,
      dipAssertions.includes(AssertionType.CREDIT_CONSENT) ? 'creditConsentAccepted' : null,
    ].filter(Boolean) as DipSubmitCheckboxValue[];
  }

  return [
    caseData?.privacyAccepted ? 'privacyAccepted' : null,
    caseData?.creditConsentAccepted ? 'creditConsentAccepted' : null,
  ].filter(Boolean) as DipSubmitCheckboxValue[];
}

function hydrateApplicationSubmitCheckboxes(caseData: BtlApplication | undefined): AssertionType[] {
  return [...new Set(caseData?.assertions?.assertions?.map(assertion => assertion.type) ?? [])];
}

function isSubmitFormEditable(caseData: BtlApplication | undefined, variant: OoSubmitVariant): boolean {
  // Fail closed: without authoritative case status the form must not be editable.
  if (!caseData?.status) {
    return false;
  }

  if (variant === 'DIP') {
    return caseData.status === ApplicationStatus.NEW;
  }

  return caseData.status === ApplicationStatus.DIP_ACCEPTED;
}

function CaseSubmitPageContent({ caseId, caseData, isCaseLoading, sidebarContext, variant }: CaseSubmitPageProps) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { env } = config;
  const formValidationContext = useContext(FormValidationContext);
  const { data: missingFieldsRes } = useBtlMissingFields(caseId);

  if (!formValidationContext) {
    throw new Error('CaseSubmitPage must be used within a FormValidationProvider');
  }

  const { setIsSubmitting, setValidationTrigger } = formValidationContext;
  setValidationTrigger('onSubmit');

  const sectionId = SUBMIT_SECTION_BY_VARIANT[variant];
  const isFormEditable = isSubmitFormEditable(caseData, variant);
  const previousSection = getAdjacentOoCaseSidebarItemId(sectionId, sidebarContext, 'previous');
  const nextSection = getAdjacentOoCaseSidebarItemId(sectionId, sidebarContext, 'next');
  const isMissingFieldsBlockingSubmit = shouldBlockSubmissionForMissingFields(
    caseData,
    missingFieldsRes?.missingFields,
    missingFieldsRes?.isComplete,
  );

  if (isCaseLoading) {
    return <Spin size='large' />;
  }

  // DIP uses BTL Submit DIP schema (includes Notes). Full application uses Additional Information schema.
  const formSchema =
    variant === 'DIP'
      ? // @ts-expect-error formatMessage JSX return is valid at runtime
        submitDipFormSchema({ formatMessage, isFormEditable })
      : additionalInformationFormSchema({
          // @ts-expect-error formatMessage JSX return is valid at runtime
          formatMessage,
          isFormEditable,
          isRefinancing: caseData?.purpose === 'REFINANCE',
          isExpat: Boolean(caseData?.isExpatriate),
          isHmoProperty: caseData?.property?.propertyType === PropertyType.HMO,
          showFullApplicationAssertions: true,
          includeBtlOnlyAssertions: false,
        });
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return (
    <BtlBaseForm
      columns={adjustedFormSchema}
      isFormEditable={isFormEditable}
      name={variant === 'DIP' ? 'oo_submit_dip' : 'oo_submit_application'}
      {...standardFormValidationProps}
      request={async () => ({
        checkboxes:
          variant === 'DIP' ? hydrateDipSubmitCheckboxes(caseData) : hydrateApplicationSubmitCheckboxes(caseData),
        // The submit API has no field for the Customer Declaration confirmation, so it can't be
        // re-hydrated. A read-only form means the DIP is already submitted, which can only happen
        // once the broker has ticked this, so show it as accepted rather than as an empty tick box.
        [CUSTOMER_DECLARATION_ACCEPTED]:
          variant === 'DIP' && !isFormEditable ? [CUSTOMER_DECLARATION_ACCEPTED] : undefined,
      })}
      onFinish={
        isFormEditable && caseData?.status
          ? async values => {
              if (isMissingFieldsBlockingSubmit) {
                message.error(formatMessage({ id: 'btlApplicationForm.submit.missingFields.blocked' }));
                return;
              }

              try {
                setIsSubmitting(true);

                const result =
                  variant === 'DIP'
                    ? await btlApplicationFormApi.submitDipRequest(caseId, {
                        assertions: {
                          privacyAccepted: (values.checkboxes ?? []).includes('privacyAccepted'),
                          creditConsentAccepted: (values.checkboxes ?? []).includes('creditConsentAccepted'),
                        },
                        notes: values.notes,
                      })
                    : await btlApplicationFormApi.submitApplicationFull(caseId, {
                        assertions: (values.checkboxes ?? []) as AssertionType[],
                        notes: values.notes,
                      });

                if (result.success) {
                  message.success(
                    `Application submitted${env.VITE_ENV === 'production' ? ' successfully' : ` with id: ${caseId}`}`,
                  );
                  await invalidateBtlApplicationData(queryClient, caseId);
                  navigate({ search: `?section=${CaseSidebarItemId.OVERVIEW}` });
                } else {
                  message.error(result.warnings.toString());
                }
              } catch (error: unknown) {
                console.error(error);
                message.error(error instanceof Error ? error.message : 'Failed to submit');
              } finally {
                setIsSubmitting(false);
              }
            }
          : undefined
      }
      submitter={
        isFormEditable
          ? {
              render: ({ form: antForm }) => (
                <OoCaseFormFooter
                  previousSection={previousSection}
                  nextSection={nextSection}
                  previousLabel={getOoCaseSidebarNavigationLabel(formatMessage, sectionId, sidebarContext, 'previous')}
                  nextLabel={getOoCaseSidebarNavigationLabel(formatMessage, sectionId, sidebarContext, 'next')}
                  form={antForm}
                  isFormEditable
                  submitLabel={formatMessage({ id: SUBMIT_LABEL_BY_VARIANT[variant] })}
                  isSubmitDisabled={isMissingFieldsBlockingSubmit}
                  submitDisabledReason={
                    isMissingFieldsBlockingSubmit
                      ? formatMessage({ id: 'btlApplicationForm.submit.missingFields.blocked' })
                      : undefined
                  }
                  onNavigate={itemId => navigate({ search: `?section=${itemId}` })}
                />
              ),
            }
          : false
      }
    />
  );
}

export function CaseSubmitPage(props: CaseSubmitPageProps) {
  return <CaseSubmitPageContent {...props} />;
}
